import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import ProductTile from "../../components/core-components/ProductTile.tsx";
import PaginatedItems from "../../components/PaginatedItems.tsx";
import Filterbar from "../../components/core-components/Filterbar.tsx";
import { translations } from "../../../utils/translations.js";
import { useSelector } from "react-redux";
import { LuFilter } from "react-icons/lu";
import {
  getColors,
  getFilamentTypes,
  getProductByCategory,
} from "../../../services/productsService.js";
import Modal from "../../components/core-components/Modal.tsx";
import {
  filterProducts,
  calculateDiscountedPrice,
  getPriceRange,
} from "../../../utils/utils.ts";

const Wrapper = styled.div`
  width: 100%;
  padding: 1rem;
`;
const CustomFilterbar = styled.div`
  padding-left: 1.5rem;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const PageTitle = styled.h1`
  margin-left: 2rem;
  color: var(--secondary-font-color);
  font-family: Georgia, serif;
  letter-spacing: 1px;
`;
const ProductsNumber = styled.p`
  color: var(--tertiary-font-color);
  font-family: Georgia, serif;
  padding-right: 8px;
  border-right: 1px solid rgba(0, 0, 0, 0.3);
`;

const Button = styled.button`
  margin-left: 1rem;
  padding: 0 8px;
  background-color: transparent;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  font-family: Georgia, serif;
  color: var(--tertiary-font-color);
  font-size: 1.5rem;
  cursor: pointer;
  svg {
    margin-top: 8px;
  }
`;

const Sort = styled.div`
  margin-right: 1rem;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  color: var(--tertiary-font-color);
  font-family: Georgia, serif;

  @media (min-width: 820px) {
    margin-left: 2rem;
  }

  select {
    padding: 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 5px;
  }

  select:focus {
    outline: none;
  }
`;

const getName = (categoryName) => {
  if (!categoryName) return "";
  const name = categoryName
    .toLowerCase()
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return name;
};

const ProductsCategory = () => {
  const languageCode = useSelector((state) => state.settings.languageCode);
  // const categories = useSelector((state) => state.data.categoryData);
  const { categoryName } = useParams();
  const navigate = useNavigate();

  // const categoryExists = categories.some(
  //   (category) =>
  //     category.categoryName.trim().toLowerCase() ===
  //     getName(categoryName).trim().toLowerCase()
  // );

  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [sortOrder, setSortOrder] = useState("recomandation");

  const [selectedPrice, setSelectedPrice] = useState("");
  const [selectedColor, setSelectedColor] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState([]);
  const [selectedRating, setSelectedRating] = useState([]);
  const [selectedCustom, setSelectedCustom] = useState([]);

  const { data: colors } = useQuery({
    queryKey: ["colors"],
    queryFn: () => getColors(),
  });

  const { data: types } = useQuery({
    queryKey: ["types"],
    queryFn: () => getFilamentTypes(),
  });

  const { data: products, isLoading } = useQuery({
    queryKey: ["products", categoryName],
    queryFn: () => getProductByCategory(getName(categoryName)),
  });
  const priceRange = useMemo(() => {
    const range = getPriceRange(products);
    setSelectedPrice(`${range[0].value}-${range[1].value}`);
    return range;
  }, [products]);

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };

  const handleSelectioPriceChange = (newSelected) => {
    setSelectedPrice(newSelected);
  };
  const handleSelectioColorChange = (newSelected) => {
    setSelectedColor(newSelected);
  };

  const handleSelectioMaterialChange = (newSelected) => {
    setSelectedMaterial(newSelected);
  };

  const handleSelectioRatingChange = (newSelected) => {
    setSelectedRating(newSelected);
  };

  const handleSelectioCustomChange = (newSelected) => {
    setSelectedCustom(newSelected);
  };

  const checkScreenSize = () => {
    setIsMobile(window.matchMedia("(max-width: 820px)").matches);
  };

  useEffect(() => {
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  const filters = useMemo(
    () => [
      {
        title: translations[languageCode]["filterbar-price"],
        type: "range",
        selectedValues: selectedPrice,
        onChange: handleSelectioPriceChange,
        isExpanded: true,
        items: priceRange,
      },
      {
        title: translations[languageCode]["filterbar-color"],
        type: "checkbox",
        selectedValues: selectedColor,
        onChange: handleSelectioColorChange,
        isExpanded: false,
        items: colors,
      },
      {
        title: translations[languageCode]["filterbar-material"],
        type: "checkbox",
        selectedValues: selectedMaterial,
        isExpanded: true,
        onChange: handleSelectioMaterialChange,
        items: types,
      },
      {
        title: translations[languageCode]["filterbar-rating"],
        type: "checkbox",
        selectedValues: selectedRating,
        isExpanded: true,
        onChange: handleSelectioRatingChange,
        items: [
          { value: "0", label: "0 stars" },
          { value: "1", label: "1 star" },
          { value: "2", label: "2 stars" },
          { value: "3", label: "3 stars" },
          { value: "4", label: "4 stars" },
          { value: "5", label: "5 stars" },
        ],
      },
      {
        title: translations[languageCode]["filterbar-custom"],
        type: "checkbox",
        selectedValues: selectedCustom,
        isExpanded: true,
        onChange: handleSelectioCustomChange,
        items: [
          {
            value: "isCustom",
            label: translations[languageCode]["filterbar-custom"],
          },
        ],
      },
    ],
    [
      selectedPrice,
      selectedColor,
      selectedMaterial,
      selectedRating,
      selectedCustom,
      priceRange,
      colors,
      types,
      languageCode,
    ]
  );

  const filteredProducts = filterProducts(
    products,
    selectedPrice,
    selectedColor,
    selectedMaterial,
    selectedRating,
    selectedCustom
  );

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      const priceA = Number(calculateDiscountedPrice(a.price, a.discount));
      const priceB = Number(calculateDiscountedPrice(b.price, b.discount));

      if (sortOrder === "ascending") return priceA - priceB;
      if (sortOrder === "descending") return priceB - priceA;

      return 0;
    });
  }, [sortOrder, filteredProducts, categoryName, products?.length]);

  // If category doesn't exist, redirect to 404 page
  // if (!categoryExists) {
  //   return <Navigate to="/404" replace />;
  // }
  return (
    <Wrapper>
      <HeaderWrapper>
        <PageTitle>{getName(categoryName)}</PageTitle>

        <Sort>
          {isMobile && (
            <Button onClick={() => setIsModalOpen(true)}>
              <LuFilter />
            </Button>
          )}
          <ProductsNumber>
            {sortedProducts.length}{" "}
            {translations[languageCode]["shop-item-number"]}
          </ProductsNumber>
          <label htmlFor="sort">
            {translations[languageCode]["filterbar-sort"]}
          </label>
          <select
            name="sort"
            id="sort"
            value={sortOrder}
            onChange={handleSortChange}
          >
            <option value="recomandation">
              {translations[languageCode]["filterbar-sort-recomandation"]}
            </option>
            <option value="ascending">
              {translations[languageCode]["filterbar-sort-ascending"]}
            </option>
            <option value="descending">
              {translations[languageCode]["filterbar-sort-descending"]}
            </option>
          </select>
        </Sort>
      </HeaderWrapper>

      <ContentWrapper>
        {!isMobile && (
          <CustomFilterbar>
            <Filterbar
              title={translations[languageCode]["filterbar-title"]}
              sections={filters}
            />
          </CustomFilterbar>
        )}
        {isMobile && isModalOpen && (
          <Modal
            name="filter-modal"
            title={translations[languageCode]["filterbar-title"]}
            isFullHeight
            isFullWidth
            content={<Filterbar sections={filters} />}
            onClose={() => setIsModalOpen(false)}
          />
        )}
        <PaginatedItems
          items={sortedProducts}
          isLoading={isLoading}
          renderItem={(product) => (
            <ProductTile
              key={product.productID}
              id={product.productID}
              name={product.productName}
              price={product.price}
              image={product.imageUrls ? product.imageUrls[0] : null}
              discount={product.discount}
              onClick={() => navigate(`${product.productID}`)}
            />
          )}
          maxItemsPerPage={30}
        />
      </ContentWrapper>
    </Wrapper>
  );
};

export default ProductsCategory;
